import { useCallback, useEffect, useState } from "react";

import { type VirtualTableSortEvent, usePrevious } from "@snapchat/snap-design-system";

import { FilterService } from "../services/FilterService";

type ValueOf<T> = T[keyof T];

const SortOrder = {
    None: "none",
    Asc: "asc",
    Desc: "desc",
};

type SortOrder = ValueOf<typeof SortOrder>;

const isComparable = (val: unknown): val is number | string | boolean => {
    return typeof val === "string" || typeof val === "number" || typeof val === "boolean";
};

interface UseTableFilterAndSort<T> {
    columnId: string;
    sortOrder: SortOrder;
    data: readonly T[];
    onColumnSort: (event: VirtualTableSortEvent) => void;
}

interface UseTableFilterAndSortProps<T> {
    tableData: readonly T[];
    initialColumn?: string;
    initialSortOrder?: SortOrder;
    searchText?: string;
    getSearchKey: (value: T) => string;
    initialRemoveFromSortKey?: string;
}

const useTableFilterAndSort = <T extends Record<string, unknown>>({
    tableData,
    initialColumn = "",
    initialSortOrder = SortOrder.Desc,
    searchText,
    getSearchKey,
    initialRemoveFromSortKey,
}: UseTableFilterAndSortProps<T>): UseTableFilterAndSort<T> => {
    const [tableState, setTableState] = useState({
        data: tableData,
        sortOrder: initialSortOrder,
        columnId: initialColumn,
    });
    const [isSorted, setIsSorted] = useState(false);
    const prev = usePrevious(searchText);

    const prevTableData = usePrevious(tableData);

    const handleSort = useCallback(
        (dataToSort: readonly T[], tableColId?: string, tableSortOrder?: SortOrder, isFirstSort?: boolean) => {
            const { columnId, sortOrder } = tableState;

            return [...dataToSort].sort((a, b) => {
                let compareA = a[tableColId ?? columnId];
                let compareB = b[tableColId ?? columnId];

                if (!isComparable(compareA)) {
                    return 0;
                }
                // Only over ride if user has clicked on a column to sort by
                if (!isFirstSort && !isSorted && initialRemoveFromSortKey && b[initialRemoveFromSortKey]) {
                    return 1;
                }

                if (typeof compareA === "string" && typeof compareB === "string") {
                    compareA = compareA.toLowerCase();
                    compareB = compareB.toLowerCase();
                }

                const direction = (tableSortOrder ?? sortOrder) === SortOrder.Desc ? 1 : -1;

                if (isComparable(compareA) && isComparable(compareB)) {
                    return compareA > compareB ? -direction : direction;
                }
                // field is not sortable
                return 0;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- disable
        [isSorted, tableState],
    );

    const onTableFilter = useCallback(() => {
        const filter = new FilterService(searchText);
        const filtered = filter.applyFilter(tableData, getSearchKey);

        const sorted = handleSort(filtered);
        setTableState((oldTableState) => ({
            ...oldTableState,
            data: sorted,
        }));
    }, [getSearchKey, handleSort, searchText, tableData]);

    const onColumnSort = useCallback(
        ({ columnId, sortOrder }: VirtualTableSortEvent) => {
            const sortData = handleSort(tableState.data, columnId, sortOrder, true);
            setTableState({
                data: sortData,
                sortOrder,
                columnId,
            });
            setIsSorted(true);
        },
        [handleSort, tableState.data],
    );

    useEffect(() => {
        // re-trigger search if table data changes
        if (JSON.stringify(prevTableData) === JSON.stringify(tableData)) {
            return;
        }
        if (searchText) {
            onTableFilter();
        } else {
            setTableState((oldTableState) => ({ ...oldTableState, data: tableData }));
        }
    }, [getSearchKey, onTableFilter, prevTableData, searchText, tableData]);

    useEffect(() => {
        // only trigger filter if search value is new
        if (prev !== searchText) {
            onTableFilter();
        }
    }, [getSearchKey, onTableFilter, prev, searchText]);

    return {
        ...tableState,
        onColumnSort,
    };
};

export { useTableFilterAndSort };
